import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontWeight, Text } from '@lili/lili-frontend-common';
import { AccordionTab } from 'primereact/accordion';
import {
  AccordionHeaderType,
  AccordionTabHeaderTemplateOneLiner,
  LiliAccordion,
} from '../../../../DesignSystem/LiliAccordion/LiliAccordion';
import Heading from '../../../../generic-components/Heading';
import { getStaticMapUrl } from '../../../../services/googleMaps.service';
import { LiliTransaction } from '../../types/lili-transaction';
import { TransactionLocationDetails } from '../../types/transaction-location-details';
import Button from '../../../../generic-components/Button';
import { useDates } from '../../hooks/useDates';
import { CheckReviewPopup } from '../shared/CheckReviewPopup';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { ExternalTransactionTypes } from '../../enum/extranal-transaction-types';

interface IProps {
  transaction: LiliTransaction;
}

export const TransactionDetails = ({ transaction }: IProps) => {
  const { t } = useTranslation();
  const [locationData, setLocationData] = useState<TransactionLocationDetails>({
    address: transaction?.locationDetails?.address,
    phoneNumber: transaction?.locationDetails?.phoneNumber,
    website: transaction?.locationDetails?.website,
    longitude: transaction?.locationDetails?.longitude,
    latitude: transaction?.locationDetails?.latitude,
  });
  const [isCheckPreview, setIsCheckPreview] = useState<boolean>(false);
  const { formatWithSpecificScheme } = useDates();

  const { address, phoneNumber, website, latitude, longitude } = locationData;

  const isSomePropAvailable = useCallback(
    () =>
      [
        address,
        phoneNumber,
        website,
        longitude,
        latitude,
        transaction.checkDetailsData?.checkTransactionId,
        transaction.checkDetailsData?.estimatedArrivalDate,
        transaction.checkDetailsData?.checkNumber,
        transaction.checkDetailsData?.operatorMessage,
        transaction?.expectedClearDate,
        transaction?.targetAchNumber,
        transaction?.sourceAchNumber,
        transaction?.targetRoutingNumber,
        transaction?.sourceRoutingNumber,
        transaction?.imadNumber,
        transaction.localAmount && transaction.currencyCode !== transaction.localCurrencyCode,
        transaction?.targetWireAccountNumber,
        transaction?.targetWireRoutingNumber,
        transaction?.swiftCode,
        transaction.iban,
        transaction.uetr,
        transaction.instructedAmountCurrencyCode,
        transaction.additionalPaymentInformation,
      ].some((i) => i),
    [transaction, locationData],
  );

  useEffect(() => {
    setLocationData({
      address: transaction?.locationDetails?.address,
      phoneNumber: transaction?.locationDetails?.phoneNumber,
      website: transaction?.locationDetails?.website,
      longitude: transaction?.locationDetails?.longitude,
      latitude: transaction?.locationDetails?.latitude,
    });
  }, [transaction]);

  const handleOpenMap = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${locationData.latitude},${locationData.longitude}`,
      '_blank',
    );
  };

  const staticMapRenderer = () => {
    const url = getStaticMapUrl({
      center: { latitude: latitude || 0, longitude: longitude || 0 },
      zoom: '16',
      size: '390x100',
    });
    return latitude && latitude ? (
      <Button
        onClick={handleOpenMap}
        className=''
        id='staticMap'
        childElement={
          <div className='w-full h-[100px]'>
            <img alt='Static Map' className='w-full h-full object-cover' src={url} />
          </div>
        }
      />
    ) : (
      ''
    );
  };
  const locationName = () =>
    (address ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='location'
          title={t('TRANSACTION_DETAILS_LOCATION')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading id='locationValue' title={address || ''} className='transaction-details-item-value-common' />
      </div>
    ) : (
      ''
    ));

  const websiteCmp = () =>
    (website ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='website'
          title={t('TRANSACTION_DETAILS_WEBSITE')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <a
          id='webSiteLink'
          className='font-metricRMedium text-[14px] text-action03 grow text-left max-w-[190px] break-words'
          href={website}
          target='_blank'
          rel='noreferrer'
        >
          Visit Website
        </a>
      </div>
    ) : (
      ''
    ));

  const phoneNumberCmp = () =>
    (phoneNumber ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='phoneNumber'
          title={t('TRANSACTION_DETAILS_PHONE_NUMBER')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading id='phoneNumberValue' title={phoneNumber} className='transaction-details-item-value-common' />
      </div>
    ) : (
      ''
    ));

  const checkPhoto = () =>
    (transaction.checkDetailsData?.checkTransactionId ? (
      <div className='w-full flex items-center'>
        <Heading
          id='checkPhoto'
          title={t('TRANSACTION_DETAILS_CHECK_PHOTO')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Button
          onClick={() => setIsCheckPreview(true)}
          className='w-fit'
          childElement={
            <Heading title='View' className='font-metricRMedium text-[14px] text-action03 grow text-left' />
          }
          id='viewPhoto'
        />
      </div>
    ) : (
      ''
    ));

  const checkArrivalDate = () =>
    (transaction.checkDetailsData?.estimatedArrivalDate ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='estimateArrival'
          title={t('TRANSACTION_ESTIMATED_ARRIVAL_DATE')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading
          id='estimateArrivalId'
          title={formatWithSpecificScheme(new Date(transaction.checkDetailsData.estimatedArrivalDate), 'MMM dd, yyyy')}
          className='transaction-details-item-value-common'
        />
      </div>
    ) : (
      ''
    ));

  const checkNumber = () =>
    (transaction.checkDetailsData?.checkNumber ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='checkNumber'
          title={t('TRANSACTION_DETAILS_CHECK_NUMBER')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading
          id='checkNumberValue'
          title={transaction.checkDetailsData.checkNumber}
          className='transaction-details-item-value-common'
        />
      </div>
    ) : (
      ''
    ));

  const rejectReason = () =>
    (transaction.checkDetailsData?.operatorMessage ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='rejectReason'
          title={t('TRANSACTION_DETAILS_REJECT_REASON')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading
          id='rejectValue'
          title={transaction.checkDetailsData?.operatorMessage}
          className='transaction-details-item-value-common'
        />
      </div>
    ) : (
      ''
    ));

  const estimateArrivalDate = () =>
    (transaction.expectedClearDate ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='arrivalDate'
          title={t('TRANSACTION_ESTIMATED_ARRIVAL_DATE')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading
          id='arrivalDateValue'
          title={formatWithSpecificScheme(new Date(transaction.expectedClearDate), 'MMM dd, yyyy')}
          className='transaction-details-item-value-common'
        />
      </div>
    ) : (
      ''
    ));

  const effectiveDate = () =>
    (transaction.effectiveDate ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='effectiveDate'
          title={t('TRANSACTION_DETAILS_EFFECTIVE_DATE')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading
          id='effectiveDateValue'
          title={formatWithSpecificScheme(new Date(transaction.effectiveDate), 'MMM dd, yyyy')}
          className='transaction-details-item-value-common'
        />
      </div>
    ) : (
      ''
    ));

  const imadNumber = () => {
    if (transaction.imadNumber) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='accountNumber'
            title={t('TRANSACTION_DETAILS_IMAD')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='accountNumberValue'
            title={transaction.imadNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    return '';
  };

  const routingNumber = () => {
    if (
      transaction.externalTransactionType === ExternalTransactionTypes.OUTGOING_WIRE &&
      transaction.targetWireRoutingNumber
    ) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='routingNumber'
            title={t('TRANSACTION_DETAILS_ROUTING_NUM')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='routingNumberId'
            title={transaction.targetWireRoutingNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    if (transaction.amount < 0 && transaction.targetRoutingNumber) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='routingNumber'
            title={t('TRANSACTION_DETAILS_ROUTING_NUM')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='routingNumberId'
            title={transaction.targetRoutingNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    if (transaction.amount > 0 && transaction.sourceRoutingNumber) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='routingNumber'
            title={t('TRANSACTION_DETAILS_ROUTING_NUM')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='routingNumberId'
            title={transaction.sourceRoutingNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    return '';
  };

  const originalCurrency = () =>
    (transaction.localAmount && transaction.currencyCode !== transaction.localCurrencyCode ? (
      <div className='w-full flex items-start justify-between'>
        <Heading
          id='originalCurrency'
          title={t('TRANSACTION_DETAILS_ORIGINAL_CURRENCY')}
          className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
        />
        <Heading
          id='originalCurrencyValue'
          title={new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: transaction.localCurrencyCode,
          }).format(transaction.localAmount)}
          className='transaction-details-item-value-common'
        />
      </div>
    ) : (
      ''
    ));

  const instructedCurrency = () => {
    console.log(transaction);
    return (
      transaction.instructedAmountCurrencyCode &&
      transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_INCOMING_WIRE && (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='instructedAmountCurrencyCode_title'
            title={t('TRANSACTION_DETAILS_ORIGINAL_CURRENCY')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='instructedAmountCurrencyCode'
            title={String(transaction.instructedAmountCurrencyCode)}
            className='transaction-details-item-value-common'
          />
        </div>
      )
    );
  };
  const accountNumber = () => {
    if (
      transaction.externalTransactionType === ExternalTransactionTypes.OUTGOING_WIRE &&
      transaction.targetWireAccountNumber
    ) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='accountNumber'
            title={t('TRANSACTION_DETAILS_ACCOUNT_NUM')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='accountNumberValue'
            title={transaction.targetWireAccountNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    if (transaction.amount < 0 && transaction.targetAchNumber) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='accountNumber'
            title={t('TRANSACTION_DETAILS_ACCOUNT_NUM')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='accountNumberValue'
            title={transaction.targetAchNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    if (transaction.amount > 0 && transaction.sourceAchNumber) {
      return (
        <div className='w-full flex items-start justify-between'>
          <Heading
            id='accountNumber'
            title={t('TRANSACTION_DETAILS_ACCOUNT_NUM')}
            className='font-metricR text-[14px] text-bw07 min-w-[180px] basis-[180px]'
          />
          <Heading
            id='accountNumberValue'
            title={transaction.sourceAchNumber}
            className='transaction-details-item-value-common'
          />
        </div>
      );
    }
    return '';
  };

  const swiftCode = () => {
    if (
      (transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_INCOMING_WIRE ||
        transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_OUTGOING_WIRE) &&
      transaction.swiftCode
    ) {
      return (
        <div className='w-full flex items-start flex-col'>
          <Text.H10
            id='routingNumber'
            title={t('TRANSACTION_DETAILS_SWIFT_CODE')}
            className='text-surface-primary-bw-07'
          >
            {t('TRANSACTION_DETAILS_SWIFT_CODE')}
          </Text.H10>
          <Text.H10
            id='routingNumberId'
            title={transaction.swiftCode}
            fontWeight={FontWeight.MEDIUM}
            className='text-surface-primary-bw-07 pt-1'
          >
            {transaction.swiftCode}
          </Text.H10>
        </div>
      );
    }

    return '';
  };

  const swiftTransactionId = () => {
    if (
      (transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_INCOMING_WIRE ||
        transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_OUTGOING_WIRE) &&
      transaction.uetr
    ) {
      return (
        <div className='w-full flex items-start flex-col '>
          <Text.H10 id='uetr' title={t('TRANSACTION_DETAILS_SWIFT_TRANSACTION')} className='text-surface-primary-bw-07'>
            {t('TRANSACTION_DETAILS_SWIFT_TRANSACTION')}
          </Text.H10>
          <Text.H10
            id='uetrval'
            title={t('TRANSACTION_DETAILS_SWIFT_TRANSACTION')}
            className='text-surface-primary-bw-07 pt-1'
            fontWeight={FontWeight.MEDIUM}
          >
            {transaction.uetr}
          </Text.H10>
        </div>
      );
    }

    return '';
  };
  const ibanNumber = () => {
    if (
      (transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_INCOMING_WIRE ||
        transaction.externalTransactionType === ExternalTransactionTypes.INTERNATIONAL_OUTGOING_WIRE) &&
      transaction.iban
    ) {
      return (
        <div className='w-full flex items-start flex-col'>
          <Text.H10
            id='routingNumber'
            title={t('TRANSACTION_DETAILS_IBAN_NUMBER')}
            className='text-surface-primary-bw-07 '
          >
            {t('TRANSACTION_DETAILS_IBAN_NUMBER')}
          </Text.H10>
          <Text.H10
            id='routingNumberId'
            title={transaction.iban}
            fontWeight={FontWeight.MEDIUM}
            className='text-surface-primary-bw-07 pt-1'
          >
            {transaction.iban}
          </Text.H10>
        </div>
      );
    }

    return '';
  };
  const achAddedNumber = () => {
    if (transaction.additionalPaymentInformation) {
      return (
        <div className='w-full flex items-start justify-between'>
          <LiliAccordion type={AccordionHeaderType.NO_BORDER}>
            <AccordionTab
              headerTemplate={() => (
                <AccordionTabHeaderTemplateOneLiner
                  Header={() => (

                    <Text.H10
                      id=' transaction.additionalPaymentInformationId'
                      title={t('TRANSACTION_DETAILS_ADDITIONAL_PAYMENT_INFORMATION')}
                      className='text-surface-primary-bw-07 break-all'
                    >
                      {t('TRANSACTION_DETAILS_ADDITIONAL_PAYMENT_INFORMATION')}
                    </Text.H10>

                  )}
                />
              )}
            >
              <div className='w-[382px]'>

                <Text.H10
                  id='additionalPaymentInformation'
                  title={transaction.additionalPaymentInformation}
                  fontWeight={FontWeight.MEDIUM}
                  className='text-surface-primary-bw-07 break-all w-[374px]'
                >
                  {transaction.additionalPaymentInformation}
                </Text.H10>
              </div>
            </AccordionTab>
          </LiliAccordion>
        </div>
      );
    }
    return '';
  };

  return isSomePropAvailable() ? (
    <div className='w-full flex flex-col pb-[20px] border-b border-bw03 gap-[12px]'>
      {isCheckPreview && (
        <UserMessage onClick={() => setIsCheckPreview(false)} id='checkPreviewWrapper'>
          <CheckReviewPopup transaction={transaction} onClose={() => setIsCheckPreview(false)} />
        </UserMessage>
      )}
      <Text.H10 id='TRANSACTION_DETAILS' fontWeight={FontWeight.SEMIBOLD} className='text-surface-primary-bw-07'>
        {t('TRANSACTION_DETAILS')}
      </Text.H10>
      {staticMapRenderer()}
      {locationName()}
      {websiteCmp()}
      {phoneNumberCmp()}
      {/** Check Details */}
      {checkPhoto()}
      {checkArrivalDate()}
      {checkNumber()}
      {rejectReason()}
      {/** --- */}

      {/** Transfer Details */}
      {estimateArrivalDate()}
      {swiftCode()}
      {ibanNumber()}
      {swiftTransactionId()}
      {effectiveDate()}
      {imadNumber()}
      {accountNumber()}
      {routingNumber()}
      {instructedCurrency()}
      {/** --- */}

      {originalCurrency()}
      {achAddedNumber()}
    </div>
  ) : null;
};
